import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

/**
 * Scroll-reveal wrapper: fades in with a rise + blur clear once the element
 * enters the viewport. Stagger siblings with `delay` (ms). Renders instantly
 * for prefers-reduced-motion and browsers without IntersectionObserver.
 */
export default function Reveal({ children, delay = 0, y = 26, className }) {
	const ref = useRef(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const reduced = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		if (reduced || typeof IntersectionObserver === "undefined") {
			setVisible(true);
			return undefined;
		}
		const io = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setVisible(true);
					io.disconnect();
				}
			},
			{ threshold: 0.12, rootMargin: "0px 0px -6% 0px" },
		);
		io.observe(ref.current);
		return () => io.disconnect();
	}, []);

	return (
		<Box
			ref={ref}
			className={className}
			$visible={visible}
			$delay={delay}
			$y={y}
		>
			{children}
		</Box>
	);
}

const Box = styled.div`
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? 0 : p.$y)}px);
  filter: blur(${(p) => (p.$visible ? 0 : 6)}px);
  transition:
    opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1) ${(p) => p.$delay}ms,
    transform 0.9s cubic-bezier(0.16, 1, 0.3, 1) ${(p) => p.$delay}ms,
    filter 0.9s cubic-bezier(0.16, 1, 0.3, 1) ${(p) => p.$delay}ms;
  will-change: transform, opacity;
`;
